import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { CartRequestAction } from '@src/types/graphql-types'
import { sendHeaderMenuEvents } from '@utils/analytics'
import messages from '@utils/messages'
import CartIcon from '@icons/CartIcon'
import NCEMiniCartDrawer from './MiniCartDrawer'
import clsx from 'clsx'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import {
  ActiveMiniCartType,
  CartType,
  CartView,
  cartRoute,
  useRouter,
} from '@src/routes'
import { isBlueCartBE } from '@utils/cartUtils'
import { QuickCartQuery } from '@src/queries/QuickCartQuery.generated'
import { CircularProgress } from '@material-ui/core'
import {
  useGlobalSnackbar,
  SnackbarType,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { extractData, OriginalError } from '@src/utils/errorHandler'
import { useAllMinicart, useQuickCart } from '@src/utils/useCart'

const useStyles = makeStyles((theme: Theme) => ({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.brand.main,
    background: 'transparent',
    borderColor: 'transparent',
    cursor: 'pointer',
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
    [theme.breakpoints.up('lg')]: {
      color: theme.palette.common.white,
    },
  },
  iconContainer: {
    display: 'flex',
    fontSize: 24,
    [theme.breakpoints.up('md')]: {
      fontSize: 22,
      marginRight: theme.spacing(2),
    },
  },
  cartLabel: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'inline',
    },
  },
  disableCart: {
    pointerEvents: 'none',
    cursor: 'default',
  },
  count: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.secondary.main,
    lineHeight: theme.typography.pxToRem(24),
    minWidth: theme.typography.pxToRem(24),
    height: theme.typography.pxToRem(24),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.brand.main}`,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(11),
      color: theme.palette.common.black,
      backgroundColor: theme.palette.common.white,
      marginLeft: theme.spacing(3),
    },
  },
  spinner: {
    color: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: theme.typography.pxToRem(24),
    height: theme.typography.pxToRem(24),
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(3),
    },
  },
  isEmpty: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
  },
}))

const MiniCartButton: React.FC<{
  id: string
  count: number
  loading: boolean
  cartType: CartType
  setMiniCartOpen: (open: boolean) => void
}> = ({ id, count, loading, cartType, setMiniCartOpen }) => {
  const classes = useStyles()
  const router = useRouter()
  const isCartPage = router.asPath.includes('action=cart')

  const handleClick = () => {
    if (isCartPage) return
    sendHeaderMenuEvents({ menu: messages.CART })

    if (count > 0) {
      return setMiniCartOpen(true)
    }
    return router.push(cartRoute[CartView.Cart](cartType))
  }

  return (
    <button
      className={clsx(classes.trigger, {
        [classes.disableCart]: isCartPage,
      })}
      aria-label="show-mini-cart"
      data-testid={id}
      id={id}
      onClick={handleClick}
    >
      <div className={classes.iconContainer}>
        <CartIcon fontSize="inherit" />
      </div>
      <span className={classes.cartLabel}>
        <FormattedMessage {...messages.CART} />
      </span>

      {loading ? (
        <CircularProgress className={classes.spinner} size={20} thickness={5} />
      ) : (
        <span
          className={clsx(classes.count, {
            [classes.isEmpty]: count === 0,
          })}
          data-testid="cart-count"
          id="cart-count"
        >
          {count}
        </span>
      )}
    </button>
  )
}

interface CartProps {
  cart: QuickCartQuery['getCart'] | null
  count: number
}

export const MiniCart: React.FC<{ id: string }> = ({ id }) => {
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const {
    anonymousShoppingDisabled,
    currentUser,
    hasOnlyBlueERP,
    isBlueErpIntegrationEnabled,
  } = useCurrentUser()

  const { isMarketplaceCartEnabled } = currentUser?.metadata ?? {}
  const initialCart = { cart: null, count: 0 }
  const initialActiveCartType = hasOnlyBlueERP
    ? ActiveMiniCartType.BLUECART
    : ActiveMiniCartType.SIAL

  const [miniCartOpen, setMiniCartOpen] = useState(false)
  const [cartData, setCartData] = useState<QuickCartQuery['getCart'] | null>()
  const [sialCart, setSialCart] = useState<CartProps>(initialCart)
  const [blueCart, setBlueCart] = useState<CartProps>(initialCart)
  const [marketplaceCart, setMarketplaceCart] = useState<CartProps>(initialCart)
  const [cartType, setCartType] = useState<CartType>(CartType.Active)
  const [activeCartType, setActiveCartType] = useState<ActiveMiniCartType>(
    initialActiveCartType
  )

  const {
    data: sialCartData,
    loading: sialCartLoading,
    error: sialCartError,
  } = useQuickCart({
    variables: { id: CartRequestAction.Mini },
    skip: anonymousShoppingDisabled || isBlueErpIntegrationEnabled,
  })
  const {
    data: marketplaceCartData,
    loading: marketplaceCartLoading,
    error: marketplaceCartError,
  } = useQuickCart({
    variables: { id: CartRequestAction.Marketplace },
    skip: !isMarketplaceCartEnabled || isBlueErpIntegrationEnabled,
  })
  const {
    data: allCartsData,
    loading: allCartsLoading,
    error: allCartsError,
  } = useAllMinicart({
    skip: !isBlueErpIntegrationEnabled || anonymousShoppingDisabled,
  })

  const isLoading = sialCartLoading || marketplaceCartLoading || allCartsLoading
  const error = sialCartError || marketplaceCartError || allCartsError
  const itemCount = [sialCart, marketplaceCart, blueCart].reduce(
    (acc, cart) => acc + cart.count,
    0
  )

  // Set cart data for each cart type if it exists
  useEffect(() => {
    if (sialCartData?.getCart) {
      setSialCart({
        cart: sialCartData?.getCart,
        count: sialCartData?.getCart?.count ?? 0,
      })
    }
    if (marketplaceCartData?.getCart) {
      setMarketplaceCart({
        cart: marketplaceCartData?.getCart,
        count: marketplaceCartData?.getCart?.count ?? 0,
      })
    }
    if (allCartsData?.getAllCarts && allCartsData?.getAllCarts.length > 0) {
      allCartsData?.getAllCarts.forEach(
        (cart: QuickCartQuery['getCart'] | null) => {
          if (!cart?.cartType) {
            return setSialCart({ cart: cart, count: cart?.count ?? 0 })
          }
          if (isBlueCartBE(cart?.cartType)) {
            return setBlueCart({ cart: cart, count: cart?.count ?? 0 })
          }
          if (cart?.cartType === CartType.MarketplaceCart) {
            return setMarketplaceCart({ cart: cart, count: cart?.count ?? 0 })
          }
        }
      )
    }
  }, [sialCartData, marketplaceCartData, allCartsData])

  // Set active cart data based on the selected cart type
  useEffect(() => {
    setCartData(sialCart.cart)
    setCartType(CartType.Active)

    if (activeCartType === ActiveMiniCartType.BLUECART && blueCart.count) {
      setCartData(blueCart.cart)
      setCartType(CartType.BlueCart)
    }

    if (
      activeCartType === ActiveMiniCartType.MARKETPLACE &&
      marketplaceCart.count
    ) {
      setCartData(marketplaceCart.cart)
      setCartType(CartType.MarketplaceCart)
    }
  }, [activeCartType, sialCart, marketplaceCart, blueCart])

  // Show error message in global snackbar
  useEffect(() => {
    if (!error) return

    const { displayableError } = extractData(error as OriginalError)
    setGlobalSnackbarState({
      open: true,
      message: displayableError ?? (
        <FormattedMessage {...messages.GENERIC_ERROR} />
      ),
      variant: SnackbarType.Error,
    })
  }, [error])

  return (
    <>
      <MiniCartButton
        id={id}
        count={itemCount}
        loading={isLoading}
        setMiniCartOpen={setMiniCartOpen}
        cartType={cartType}
      />

      {cartData ? (
        <NCEMiniCartDrawer
          open={miniCartOpen}
          cart={cartData}
          cartType={cartType}
          activeCartType={activeCartType}
          sialCount={sialCart.count}
          mCount={marketplaceCart.count}
          blueCartCount={blueCart.count}
          closeMiniCart={() => setMiniCartOpen(false)}
          toggleCart={(activeCartType) => setActiveCartType(activeCartType)}
        />
      ) : null}
    </>
  )
}
export default MiniCart
